import React from 'react'
import api from 'api/api'
import useStore from 'state/knovStore'
import StreamJoinModal from 'components/teams/StreamJoinModal'
import { channelJoinInviteReason } from 'state/channelJoinInvite'

// Records a fresh membership where the client's permission checks read it: the
// full channel (posting and reply rules included) in gon.currentUser.space_teams,
// which teamPermsFromFilterTeamId prefers, and in userStreams. Replaces any
// partial entry, so call it again once the hydrated channel arrives.
export function recordChannelMembership(team) {
    if (!team?.id) return
    const teamId = String(team.id)
    const user = window.gon?.currentUser
    if (user) {
        const others = (user.space_teams || []).filter(t => String(t.id) !== teamId)
        user.space_teams = [...others, team]
    }
    const streams = useStore.getState().userStreams || []
    useStore.getState().set({
        userStreams: [...streams.filter(stream => String(stream.id) !== teamId), team],
    })
}

// Opens the join card for +team+ because of something the user just tried
// (e.g. starting a thread where only members post). +afterJoin+ runs once they
// have joined, with membership already recorded for the client's checks.
export function requestChannelJoin(team, { reasonText = undefined, afterJoin = undefined } = {}) {
    if (!team?.id) return
    useStore.getState().set({
        channelJoinInvite: {
            teamId: String(team.id),
            teamName: team.name || 'this channel',
            milestone: 1,
            activityCount: 0,
            trigger: 'request',
            fresh: true,
            reasonText,
            afterJoin,
            team,
        },
    })
}

// The channel join prompt (state/channelJoinInvite): shown when a post or upvalue
// response carries one.
// Joining puts the channel in the user's Following.
export default function ChannelJoinInviteModal() {
    const invite = useStore(state => state.channelJoinInvite)

    if (!invite) return null

    const close = () => useStore.getState().set({ channelJoinInvite: null })

    const handleJoined = async () => {
        close()
        // The full channel when the card was opened with one; the server's queued
        // prompt carries only id and name until the fetch below hydrates it.
        recordChannelMembership(invite.team || { id: invite.teamId, name: invite.teamName })
        useStore.getState().showGlobalNotice(`Joined ${invite.teamName}`, {
            kind: 'channel-join-invite',
        })
        invite.afterJoin?.()
        const team = await api.getTeam(invite.teamId).catch(() => null)
        if (team?.id) recordChannelMembership(team)
    }

    return (
        <StreamJoinModal
            isOpen
            team={{ id: invite.teamId, name: invite.teamName }}
            onDismiss={close}
            onJoined={handleJoined}
            reason={channelJoinInviteReason(invite)}
            readFirstLabel="Not now"
        />
    )
}
